import { motion } from "framer-motion";
import { Waves, Flame, Droplets } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const POOL =
    "https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/93cxdlpi_Imagen%2021-4-26%20a%20las%2021.20.jpeg";
const SPA =
    "https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/xmyy4tgc_Imagen%2021-4-26%20a%20las%2021.20.jpeg";
const SHOWERS =
    "https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/psgo8elq_Imagen%2021-4-26%20a%20las%2021.19.jpeg";

function AmenityCard({ image, label, title, body, icon: Icon, testId, className }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className={`group relative overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700 bg-charcoal-surface ${className}`}
            data-testid={testId}
        >
            <div className="absolute inset-0">
                <img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-[1400ms]"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-charcoal/10" />
            </div>
            <div className="relative z-10 p-8 md:p-10 min-h-[440px] md:min-h-[480px] flex flex-col justify-end">
                <div className="flex items-center gap-3 mb-4">
                    <Icon size={16} className="text-gold" strokeWidth={1.6} />
                    <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                        {label}
                    </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-tight text-white max-w-md">
                    {title}
                </h3>
                <p className="mt-4 text-white/70 text-sm md:text-base max-w-lg leading-relaxed">
                    {body}
                </p>
            </div>
        </motion.article>
    );
}

export default function Amenities() {
    const { t } = useT();
    return (
        <section
            id="amenities"
            className="relative py-24 md:py-32 bg-charcoal"
            data-testid="amenities-section"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="inline-block w-8 h-[1px] bg-gold" />
                            <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                                {t("amenities.overline")}
                            </span>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-semibold max-w-3xl leading-[1.05]">
                            {t("amenities.heading_line1")}{" "}
                            <span className="italic font-normal text-gold">
                                {t("amenities.heading_highlight")}
                            </span>
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
                        {t("amenities.subhead")}
                    </p>
                </div>

                {/* Bento: pool (wide hero) + spa + showers */}
                <div className="grid grid-cols-12 gap-4 md:gap-6">
                    <AmenityCard
                        image={POOL}
                        icon={Waves}
                        label={t("amenities.pool_label")}
                        title={t("amenities.pool_title")}
                        body={t("amenities.pool_body")}
                        testId="amenity-pool"
                        className="col-span-12"
                    />
                    <AmenityCard
                        image={SPA}
                        icon={Flame}
                        label={t("amenities.spa_label")}
                        title={t("amenities.spa_title")}
                        body={t("amenities.spa_body")}
                        testId="amenity-spa"
                        className="col-span-12 md:col-span-6"
                    />
                    <AmenityCard
                        image={SHOWERS}
                        icon={Droplets}
                        label={t("amenities.shower_label")}
                        title={t("amenities.shower_title")}
                        body={t("amenities.shower_body")}
                        testId="amenity-showers"
                        className="col-span-12 md:col-span-6"
                    />
                </div>
            </div>
        </section>
    );
}
